import { Badge, Typography } from '@lucid/lego';

export interface Claim {
  id: string;
  claimId: string;
  vin: string;
  lucidId: string;
  claimType: string;
  submissionDate: string;
  status: string;
  amount: string;
  reviewer: string;
}

const getStatusBadgeColor = (status: string): 'error' | 'warning' | 'informative' | 'success' => {
  switch (status) {
    case 'under-review':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    case 'info-required':
      return 'error';
    case 'draft':
      return 'informative';
    default:
      return 'informative';
  }
};

const getClaimTypeBadgeColor = (
  claimType: string
): 'error' | 'warning' | 'informative' | 'success' => {
  switch (claimType.toLowerCase()) {
    case 'warranty':
      return 'informative';
    case 'goodwill':
      return 'success';
    case 'recall':
      return 'error';
    case 'campaign':
      return 'warning';
    default:
      return 'informative';
  }
};

const formatStatus = (status: string): string => {
  return status
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface CreateColumnsParams {
  theme: 'light' | 'dark';
  onViewClick: (claim: Claim) => void;
}

export const createColumns = ({ theme, onViewClick }: CreateColumnsParams) => [
  {
    key: 'claimId',
    title: 'Claim ID',
    width: '140px',
    sortable: true,
    render: (_: unknown, record: Claim) => (
      <Typography
        variant="body"
        size="small"
        color="blue"
        theme={theme}
        onClick={() => onViewClick(record)}
      >
        {record.claimId}
      </Typography>
    ),
  },
  {
    key: 'vin',
    title: 'VIN',
    width: '180px',
    sortable: true,
    render: (_: unknown, record: Claim) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.vin}
      </Typography>
    ),
  },
  {
    key: 'lucidId',
    title: 'Lucid ID',
    width: '140px',
    render: (_: unknown, record: Claim) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.lucidId}
      </Typography>
    ),
  },
  {
    key: 'claimType',
    title: 'Claim Type',
    width: '120px',
    align: 'center',
    render: (_: unknown, record: Claim) => (
      <Badge
        label={record.claimType}
        color={getClaimTypeBadgeColor(record.claimType)}
        type="secondary"
        theme={theme}
      />
    ),
  },
  {
    key: 'submissionDate',
    title: 'Submission Date',
    width: '160px',
    sortable: true,
    render: (_: unknown, record: Claim) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.submissionDate}
      </Typography>
    ),
  },
  {
    key: 'status',
    title: 'Status',
    width: '150px',
    align: 'center',
    sortable: true,
    render: (_: unknown, record: Claim) => (
      <Badge
        label={formatStatus(record.status)}
        color={getStatusBadgeColor(record.status)}
        type="secondary"
        theme={theme}
      />
    ),
  },
  {
    key: 'amount',
    title: 'Amount',
    width: '120px',
    render: (_: unknown, record: Claim) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.amount}
      </Typography>
    ),
  },
  {
    key: 'reviewer',
    title: 'Reviewer',
    width: '160px',
    render: (_: unknown, record: Claim) => (
      <Typography variant="body" size="small" color="black" theme={theme}>
        {record.reviewer}
      </Typography>
    ),
  },
];
